import { toRefs, ref, inject, computed, watch, onMounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import VText from '../v-text/component-lib.esm.js';
import CheckIcon from '../../icons/component-lib.esm28.js';
import tinycolor from 'tinycolor2';
import { useStyleTag } from '../../compositions/component-lib.esm10.js';
import { getGoogleFontUrl } from '../../utils/component-lib.esm18.js';
import { useTheme } from '../../compositions/component-lib.esm3.js';
import { getThemeColor } from '../../styles/theme/component-lib.esm.js';

//
var script = {
  components: {
    VText,
    CheckIcon
  },
  props: {
    value: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: null
    },
    weight: {
      type: [String, Number, Array],
      default: '400'
    },
    component: {
      type: String,
      default: 'div'
    },
    color: {
      type: String,
      default: 'primary'
    },
    selectable: Boolean,
    selected: Boolean,
    loadAllWeights: Boolean,
    isGoogleFont: Boolean
  },
  setup(props, _ref) {
    let {
      emit
    } = _ref;
    const {
      component,
      color,
      value,
      selectable,
      selected,
      weight,
      loadAllWeights,
      isGoogleFont
    } = toRefs(props);
    const rootRef = ref(null);
    const isVisible = ref(false);
    const theme = useTheme();
    const styleTag = useStyleTag();
    const {
      selectedFont,
      onSelect,
      isInGroup
    } = inject('gridOptions', {
      isInGroup: false
    });
    const {
      stop
    } = useIntersectionObserver(rootRef, _ref2 => {
      let [{
        isIntersecting
      }] = _ref2;
      isVisible.value = isIntersecting;
      if (isIntersecting) {
        stop();
      }
    }, {
      rootMargin: '100px',
      threshold: 0
    });
    const isSelected = computed(() => {
      if (selected.value) return true;
      if (!isInGroup) return false;
      return selectedFont.value === value.value;
    });
    const tag = computed(() => {
      if (selectable.value) return 'button';
      return component.value;
    });
    const attributes = computed(() => {
      const attrs = {};
      if (selectable.value) {
        attrs.type = 'button';
      }
      return attrs;
    });
    const fontWeight = computed(() => {
      if (Array.isArray(weight.value)) {
        if (weight.value.includes(400) || weight.value.includes('regular')) return 400;
        return +weight.value[0];
      }
      return weight.value;
    });
    const styles = computed(() => {
      const colorValue = getThemeColor(color.value, theme.value);
      return {
        '--c-color': tinycolor(colorValue).setAlpha(0.75).toRgbString(),
        '--c-higlight-color': colorValue,
        '--c-bg-color': tinycolor(colorValue).setAlpha(0.05).toRgbString(),
        '--c-font-weight': fontWeight.value
      };
    });
    const fontStyles = computed(() => {
      return {
        fontFamily: `'${value.value}'`
      };
    });
    const classes = computed(() => {
      return {
        'v-font-preview--selectable': selectable.value,
        'v-font-preview--selected': isSelected.value
      };
    });
    function loadFont() {
      if (!isGoogleFont.value || !styleTag.value || !isVisible.value) return;

      // Make sure the fontWeight is passed as an array
      const localFontWeight = Array.isArray(weight.value) && loadAllWeights.value ? weight.value : [fontWeight.value.toString()];
      const googleFontUrl = getGoogleFontUrl(value.value, localFontWeight);
      styleTag.value.innerHTML = `@import "${googleFontUrl}";`;
    }
    watch([value, isVisible], () => loadFont(), {
      immediate: true
    });
    onMounted(() => loadFont());
    function handleClick() {
      if (isInGroup) onSelect(value.value);
      emit('click', value.value);
    }
    return {
      tag,
      attributes,
      styles,
      fontStyles,
      classes,
      isSelected,
      handleClick,
      rootRef
    };
  }
};
export { script as default };